/**
 * Top bar, left nav, theme toggle, notification bell.
 *
 * **The nav is the officer's, in the order of their day.** What they open every
 * morning comes first — the cases, a new case, a scan to turn into Word — and
 * what the office sets up once and then leaves comes under a quiet heading
 * below it. Every officer is an administrator, so nothing is hidden by role;
 * the grouping only says which is which.
 *
 * It used to list the whole product, with a build-phase code (`P10`, `P08`)
 * beside two items and a link to a screen that does not exist yet. A phase code
 * is the builders' vocabulary, and a menu item that opens *No phase is assigned
 * to this screen yet* teaches an officer that the menu cannot be trusted
 * (walkthrough 32.34). The rule the old comment reached for — what is missing
 * stays visible with its reason — is about the case: a skipped document, a
 * blocked act. It is not a reason to put the roadmap in the menu. The corpus
 * route still answers at its address; it is only out of the nav until there is
 * a screen behind it.
 */

import { useEffect, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { NotificationView, UserView } from '../api/client'
import { currentTheme, setTheme } from '../lib/theme'
import { readDate } from '../lib/period'
import { readDay } from '../lib/blocks'
import { readAsOf, writeAsOf } from '../lib/asOf'

interface NavItem {
  to: string
  label: string
}

/** The day's work. *OCR to Word* is a tool an officer uses on a paper before it
 *  enters a case — work, not setup — so it sits here. */
const DAILY: NavItem[] = [
  { to: '/cases', label: 'Cases' },
  { to: '/cases/new', label: 'New case' },
  { to: '/ocr-tool', label: 'OCR to Word' },
]

/** What the office sets up and then leaves: its documents' wording, the path a
 *  proceeding takes, the factory's own details, and the record of who did what. */
const SETUP: NavItem[] = [
  { to: '/templates', label: 'Templates' },
  { to: '/process', label: 'Process' },
  { to: '/settings', label: 'Settings' },
  { to: '/audit', label: 'Audit' },
]

function NavItems({ items }: { items: NavItem[] }) {
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/cases'}
          className={({ isActive }) => (isActive ? 'active' : '')}
        >
          <span>{item.label}</span>
        </NavLink>
      ))}
    </>
  )
}

/** The menu: the day's work, then — under a quiet heading — the office's setup. */
export function LeftNav() {
  return (
    <nav className="leftnav">
      <div className="group">Proceedings</div>
      <NavItems items={DAILY} />
      <div className="group" data-testid="nav-setup">
        Office setup
      </div>
      <NavItems items={SETUP} />
    </nav>
  )
}

export function Chrome({ user, children }: { user: UserView; children: React.ReactNode }) {
  return (
    <div className="shell">
      <TopBar user={user} />
      <LeftNav />
      <main className="main">
        <AsOfBanner />
        {children}
      </main>
    </div>
  )
}

/**
 * **Test setups only** — the day the app is working as of, so a case from
 * 2024 is walked as it happened. The server says whether it listens
 * (`as_of_enabled`, never in production) and returns the day it used; the
 * banner says so on every page, because a screen that silently runs on
 * another day is the defect this product exists to prevent.
 */
function useHealth() {
  return useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: 60_000 })
}

function AsOfControl() {
  const queryClient = useQueryClient()
  const health = useHealth()
  const [value, setValue] = useState(readAsOf() ?? '')
  if (!health.data?.['as_of_enabled']) return null
  const realToday = String(health.data?.['real_today'] ?? '')
  function choose(next: string) {
    setValue(next)
    writeAsOf(next || null)
    // Everything on screen was read for the old day.
    void queryClient.invalidateQueries()
  }
  return (
    <label className="as-of" data-testid="as-of">
      <span className="small">Working as of</span>
      <input
        type="date"
        value={value}
        max={realToday || undefined}
        onChange={(event) => choose(event.target.value)}
        aria-label="Working as of"
      />
      {value ? (
        <button type="button" className="quiet" onClick={() => choose('')}>
          Today
        </button>
      ) : null}
    </label>
  )
}

function AsOfBanner() {
  const health = useHealth()
  const chosen = readAsOf()
  if (!health.data?.['as_of_enabled'] || !chosen) return null
  return (
    <div className="as-of-banner" role="status" data-testid="as-of-banner">
      Test setup — the app is working as of <strong>{readDate(chosen)}</strong>. Deadlines, the
      date on new drafts and a new case&apos;s file number follow this day.
    </div>
  )
}

function TopBar({ user }: { user: UserView }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [theme, setLocalTheme] = useState(currentTheme())

  const signOut = useMutation({
    mutationFn: () => api.logout(),
    onSettled: () => {
      queryClient.clear()
      navigate('/login')
    },
  })

  function toggle() {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    setLocalTheme(next)
  }

  return (
    <header className="topbar">
      <div className="org">
        {/* The signed-in officer's own factory, from their identity — never a
            build-time constant. One deployment serves nine factories, and the
            shell naming this one is what tells the officer the scope of every
            screen below it: everything they can reach belongs to it. */}
        <span className="main">{user.unit_name} — Disciplinary Section</span>
        <span className="sub">a unit of AWEIL</span>
      </div>
      <div className="spacer" />
      {/* The section administers its own factory's accounts; every other role
          reaches only its own password. Drawn by role, and refused again by the
          server (CP-ROLE-01) — this is the convenience, not the boundary. */}
      {user.role === 'ds_officer' ? (
        <Link className="quiet" to="/accounts">
          Accounts
        </Link>
      ) : (
        <Link className="quiet" to="/account/password">
          Password
        </Link>
      )}
      <AsOfControl />
      <NotificationBell />
      <button type="button" className="quiet" onClick={toggle} aria-label="Switch theme">
        {theme === 'dark' ? 'Light' : 'Dark'}
      </button>
      <span className="small muted" data-testid="signed-in">
        {user.display_name}
        {user.post ? ` · ${user.post}` : ''}
      </span>
      <button type="button" className="quiet" onClick={() => signOut.mutate()}>
        Sign out
      </button>
    </header>
  )
}

/**
 * In-app only. No email, no SMS — the pilot runs on one box in a factory and a
 * notification that leaves it is a notification nobody agreed to send.
 *
 * The count polls by refetch rather than a socket: fifteen officers and a
 * thirty-second interval is a rounding error, and a socket is a second
 * connection to keep alive through a departmental proxy.
 */
function NotificationBell() {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.listNotifications(),
    refetchInterval: 30_000,
    // Asked for in the background too. A draft takes ten minutes and the officer
    // is told he may go and do something else; with the tab out of sight the
    // list stopped being asked for, and the count on the tab never changed.
    refetchIntervalInBackground: true,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const rows = notifications.data ?? []
  const unread = rows.filter((row) => !row.read_at)

  // The count on the browser tab itself, so it is seen from another tab or
  // another window: "(2) Disciplinary Section — AWEIL".
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '')
    document.title = unread.length ? `(${unread.length}) ${base}` : base
  }, [unread.length])

  return (
    <>
      <span className="bell">
        <button
          type="button"
          className="quiet"
          onClick={() => setOpen(true)}
          aria-label={`Notifications — ${unread.length} unread`}
          data-testid="bell"
        >
          Notifications
        </button>
        {unread.length ? <span className="count">{unread.length}</span> : null}
      </span>
      {open ? (
        <>
          <div className="scrim" onClick={() => setOpen(false)} />
          <aside className="drawer" role="dialog" aria-label="Notifications">
            <header>
              <h2>Notifications</h2>
              <button type="button" className="quiet" onClick={() => setOpen(false)}>
                Close
              </button>
            </header>
            <div className="body">
              {rows.length === 0 ? (
                <p className="empty">Nothing outstanding.</p>
              ) : (
                rows.map((row) => (
                  <NotificationRow
                    key={row.id}
                    row={row}
                    onRead={() => markRead.mutate(row.id)}
                  />
                ))
              )}
            </div>
          </aside>
        </>
      ) : null}
    </>
  )
}

function NotificationRow({ row, onRead }: { row: NotificationView; onRead: () => void }) {
  const payload = row.payload ?? {}
  // `message` is written for every kind now — by the emitter where it has the
  // clock's own wording or the acts a case is blocked on, and otherwise by
  // `for_everyone` from `KINDS`. Falling back to `row.kind` printed the literal
  // string `clock.overdue` twice and a date, and printed
  // `case.cannot-be-tracked` on the one surface that exists to say which act a
  // case is waiting for. The fallback stays as a last resort for a row written
  // before the sentence existed; it is no longer the normal path.
  const text = typeof payload['message'] === 'string' ? payload['message'] : row.kind
  return (
    <div
      className="card"
      style={{ marginBottom: 10, opacity: row.read_at ? 0.6 : 1 }}
      data-testid="notification"
    >
      <div className="body">
        <div className="small mono muted">{row.kind}</div>
        <div>{text}</div>
        {typeof payload['href'] === 'string' && payload['href'].startsWith('/') ? (
          /* Where the notice leads, when the emitter said: a draft that is back
             opens on the draft. An in-app path only. */
          <Link
            to={payload['href']}
            className="link"
            onClick={onRead}
            data-testid="notification-open"
          >
            Open it
          </Link>
        ) : null}
        <div className="small faint">{readDate(readDay(row.created_at))}</div>
        {!row.read_at ? (
          <button type="button" className="link" onClick={onRead} style={{ marginTop: 6 }}>
            Mark read
          </button>
        ) : null}
      </div>
    </div>
  )
}
